import { Alert, Stack, Typography } from "@mui/material";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { ManualDispatchCardItem } from "tng-address-collection-shared/types/apiTypes.ts";
import { useManualDispatchCards } from "../hooks/useManualDispatchCards.ts";
import { useManualDispatchColumns } from "../hooks/useManualDispatchColumns.tsx";
import { useTranslator } from "../i18n/translator.ts";
import { CardsTable } from "./CardsTable.tsx";
import { CardsTableToolbar } from "./CardsTableToolbar.tsx";
import { ErrorDisplay } from "./ErrorDisplay.tsx";

const compareManualDispatchCards = (
  cardA: ManualDispatchCardItem,
  cardB: ManualDispatchCardItem,
) => {
  const isSentA = cardA.manuallySentAt != null;
  const isSentB = cardB.manuallySentAt != null;
  if (isSentA !== isSentB) {
    return isSentA ? 1 : -1;
  }
  return (
    cardA.lastName.localeCompare(cardB.lastName) ||
    cardA.firstName.localeCompare(cardB.firstName)
  );
};

export const ManualDispatchPage = () => {
  const translator = useTranslator();
  const { data, error, isFetching, isPending, refetch } =
    useManualDispatchCards();
  const columns = useManualDispatchColumns();

  const cards = data?.cards;
  const sortedCards = useMemo(
    () => (cards ?? []).slice().sort(compareManualDispatchCards),
    [cards],
  );
  const isManualDispatchActive = (data?.activatedAt ?? null) != null;

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable<ManualDispatchCardItem>({
    data: sortedCards,
    getRowId: (row) => row.id,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  return (
    <Stack padding={2} spacing={2} height="100%">
      <Typography variant="h4">
        {translator("manualDispatchPageTitle")}
      </Typography>
      <CardsTableToolbar
        onReload={() => void refetch()}
        isReloading={isFetching}
      />
      {error ? (
        <ErrorDisplay error={error} />
      ) : isPending ? null : (
        <>
          {isManualDispatchActive ? (
            <Alert severity="info">
              {translator("manualDispatchActiveInfo")}
            </Alert>
          ) : (
            <Alert severity="warning">
              {translator("manualDispatchNotActiveWarning")}
            </Alert>
          )}
          {sortedCards.length === 0 && (
            <Typography color="text.secondary">
              {translator("manualDispatchEmptyList")}
            </Typography>
          )}
          {isManualDispatchActive && sortedCards.length > 0 && (
            <CardsTable
              table={table}
              isRowDeemphasized={(card, cellIndex) =>
                card.manuallySentAt !== null && cellIndex >= 1
              }
            />
          )}
        </>
      )}
    </Stack>
  );
};
